import { useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { uniqBy } from 'lodash'
import { getAuthenticationLabel } from '../../../charging-sessions/utils/get-authentication-label'
import { NOT_AUTHENTICATED_FLAG } from '../../../charging-sessions/models/not-authenticated-flag'
import { ChargingSessionModel } from '../../../charging-sessions/models/charging-sessions.model'
import { EASY_CONNECT_RFID_LENGTH } from '../../api/dto/easy-connect-rfid-length'
import { useFetchChargingCards } from '../../../charging-cards/api/use-fetch-charging-cards'
import { authenticationSortFn } from '../../utils/authentication-sort'

type AuthenticationFilterOptionsHook = {
  authenticationOptions: { label: string; value: string }[]
  isChargingCardsLoading: boolean
}

/**
 * Hook used to populate the "Authentication" dropdown seen in the E-Mobility section("Dashboard" and "Charging sessions" tab).
 * It takes the list of all sessions found in a single installation, takes list of all connected charging cards, filters for unique RFID cards used to start each session and
 * formats the result for easier consumption by the FilterButton shell component.
 *
 * However, the filtering/grouping logic seen below is not trivial, e.g. "find unique RFID strings in the session array":
 * When an RFID card is presented to the EasyConnect wallbox, only the first 8 characters of the RFID are read and sent to backend.
 * However, this same card can be presented to a newer wallbox such as MultiConnect, for which all 32 characters of the RFID are read
 * and then stored in the database.
 *
 * Since both the 8-character long RFID and the 32-character-long RFID represent the same card, we will show the longer RFID in the
 * dropdown to uniquely describe both, so the "short RFID equivalent" will not be shown in the dropdown.
 *
 * This long RFID value will then be used to filter session containing either the long or the short version of the RFID.
 */
export const useAuthenticationFilterOptions = (
  unfilteredData: ChargingSessionModel[] | undefined,
  installationId: number
): AuthenticationFilterOptionsHook => {
  const { t } = useCustomTranslation()
  const { chargingCards, isLoading } = useFetchChargingCards(Number(installationId))

  return useMemo(() => {
    const chargingCardsFromSessions =
      unfilteredData?.map(session => ({
        rfid: session.authentication.rfid,
        cardName: session.authentication.cardName,
        authenticationMethod: session.authentication.authenticationMethod,
        isAuthenticated: session.authentication.isAuthenticated,
        value: session.authentication.isAuthenticated ? session.authentication.ocpiTokenId : NOT_AUTHENTICATED_FLAG,
      })) || []

    const connectedChargingCards =
      chargingCards.map(card => ({
        rfid: card.tokenId || undefined,
        cardName: card.cardName || undefined,
        authenticationMethod: 'RFID' as const,
        isAuthenticated: true,
        value: card.tokenId ? card.ocpiTokenId : NOT_AUTHENTICATED_FLAG,
      })) || []

    const allChargingCards = chargingCardsFromSessions.concat(connectedChargingCards).sort(authenticationSortFn)

    const availableChargingCards = uniqBy(allChargingCards, cards => cards.value.slice(0, EASY_CONNECT_RFID_LENGTH))

    const authenticationOptions: { label: string; value: string }[] = availableChargingCards.map(card => ({
      label: getAuthenticationLabel({
        rfid: card.rfid,
        cardName: card.cardName,
        t,
        authenticationMethod: card.authenticationMethod,
        isAuthenticated: card.isAuthenticated,
      }),
      value: card.value,
    }))

    /** place no authentication filter option as the last option in filter */
    const indexOfNoAuthFilter = authenticationOptions.map(auth => auth.value).indexOf(NOT_AUTHENTICATED_FLAG)

    if (indexOfNoAuthFilter !== -1) {
      authenticationOptions.splice(indexOfNoAuthFilter, 1)
      authenticationOptions.push({ label: t('emobility.chargingSession.noAuthentication'), value: NOT_AUTHENTICATED_FLAG })
    }

    return { authenticationOptions, isChargingCardsLoading: isLoading || !chargingCards }
  }, [chargingCards, isLoading, t, unfilteredData])
}
